import { makeChoices } from "../../distractors";
import { numUnit, text } from "../../format";
import type { QuestionDraft, Rng, TopicDef } from "../../types";
import { both, numeric, q } from "../helpers";

const DEG = { pl: "°", en: "°" };
const deg = (n: number) => numUnit(n, "°");
const degText = (n: number) => ({ pl: `${n}°`, en: `${n}°` });

function tier1(rng: Rng): QuestionDraft {
  const a = rng.int(20, 110);
  const b = rng.int(20, 160 - a);
  const c = 180 - a - b;
  const choice4 = makeChoices(
    rng,
    c,
    [360 - a - b, 90 - Math.min(a, b), a + b, c + 10],
    (r) => r.int(10, 150),
    degText,
  );
  return q(
    both(
      `Dwa kąty trójkąta mają ${a}° i ${b}°. Ile ma trzeci kąt?`,
      `Two angles of a triangle are ${a}° and ${b}°. What is the third angle?`,
    ),
    numeric(c, DEG),
    [`Suma kątów w trójkącie to 180°.`, `180° − ${a}° − ${b}° = ${c}°.`],
    [`The angles of a triangle add up to 180°.`, `180° − ${a}° − ${b}° = ${c}°.`],
    { choice4 },
  );
}

function tier2(rng: Rng): QuestionDraft {
  if (rng.chance(0.5)) {
    const apex = rng.int(10, 80) * 2;
    const base = (180 - apex) / 2;
    const choice4 = makeChoices(
      rng,
      base,
      [180 - apex, apex / 2, Math.abs(90 - apex), base + 5],
      (r) => r.int(10, 85),
      degText,
    );
    return q(
      both(
        `W trójkącie równoramiennym kąt między ramionami ma ${apex}°. Ile ma każdy kąt przy podstawie?`,
        `In an isosceles triangle the angle between the equal sides is ${apex}°. What is each base angle?`,
      ),
      numeric(base, DEG),
      [`Kąty przy podstawie są równe.`, `(180° − ${apex}°) : 2 = ${base}°.`],
      [`The base angles are equal.`, `(180° − ${apex}°) ÷ 2 = ${base}°.`],
      { choice4 },
    );
  }
  const base = rng.int(20, 85);
  const apex = 180 - 2 * base;
  const choice4 = makeChoices(
    rng,
    apex,
    [180 - base, 90 - base, base, apex + 10],
    (r) => r.int(10, 150),
    degText,
  );
  return q(
    both(
      `W trójkącie równoramiennym kąt przy podstawie ma ${base}°. Ile ma kąt między ramionami?`,
      `In an isosceles triangle a base angle is ${base}°. What is the angle between the equal sides?`,
    ),
    numeric(apex, DEG),
    [`Oba kąty przy podstawie mają po ${base}°: razem ${2 * base}°.`, `180° − ${2 * base}° = ${apex}°.`],
    [`Both base angles are ${base}°: together ${2 * base}°.`, `180° − ${2 * base}° = ${apex}°.`],
    { choice4 },
  );
}

function tier3(rng: Rng): QuestionDraft {
  if (rng.chance(0.5)) {
    const a = rng.int(15, 75);
    const other = 90 - a;
    const choice4 = makeChoices(rng, other, [180 - a, a, 90 + a, other + 5], (r) => r.int(10, 80), degText);
    return q(
      both(
        `W trójkącie prostokątnym jeden kąt ostry ma ${a}°. Ile ma drugi kąt ostry?`,
        `In a right triangle one acute angle is ${a}°. What is the other acute angle?`,
      ),
      numeric(other, DEG),
      [`Kąt prosty ma 90°, więc na dwa kąty ostre zostaje 180° − 90° = 90°.`, `90° − ${a}° = ${other}°.`],
      [`The right angle is 90°, so the two acute angles share 180° − 90° = 90°.`, `90° − ${a}° = ${other}°.`],
      { choice4 },
    );
  }
  const a = rng.int(25, 100);
  const b = rng.int(25, 150 - a);
  const exterior = a + b;
  const choice4 = makeChoices(
    rng,
    exterior,
    [180 - a - b, 180 - a, 360 - a - b, exterior - 10],
    (r) => r.int(30, 170),
    degText,
  );
  return q(
    both(
      `Dwa kąty trójkąta mają ${a}° i ${b}°. Ile ma kąt zewnętrzny przy trzecim wierzchołku?`,
      `Two angles of a triangle are ${a}° and ${b}°. What is the exterior angle at the third vertex?`,
    ),
    numeric(exterior, DEG),
    [
      `Trzeci kąt: 180° − ${a}° − ${b}° = ${180 - a - b}°.`,
      `Kąt zewnętrzny dopełnia go do 180°: 180° − ${180 - a - b}° = ${exterior}°.`,
      `Czyli kąt zewnętrzny = suma dwóch pozostałych kątów.`,
    ],
    [
      `Third angle: 180° − ${a}° − ${b}° = ${180 - a - b}°.`,
      `The exterior angle completes it to 180°: 180° − ${180 - a - b}° = ${exterior}°.`,
      `So the exterior angle = the sum of the two remote angles.`,
    ],
    { choice4 },
  );
}

void deg;

export const anglesTriangle: TopicDef = {
  id: "sp6.angles_triangle",
  level: "sp6",
  name: text("Kąty w trójkącie", "Angles in a triangle"),
  modes: ["numeric", "choice4"],
  generate: (rng, tier) => (tier === 1 ? tier1(rng) : tier === 2 ? tier2(rng) : tier3(rng)),
};
